import React, {useCallback, useEffect, useMemo, useState} from 'react';
import '@xyflow/react/dist/style.css';
import {Template} from "../../../../components/template/template.jsx";
import {ReactFlow, useEdgesState, useNodesState, addEdge, ConnectionMode, Panel, Background} from "@xyflow/react";
import {useAnalysis} from "./hooks/use-analysis.js";
import {toast} from "react-toastify";
import {useQuery} from "@tanstack/react-query";
import associationNetworkService from "../../../../services/web/association-network-service.js";
import Vehicle from "./components/nodes/vehicle.jsx";
import Individual from "./components/nodes/individual.jsx";
import Sos from "./components/nodes/sos.jsx";
import Encounter from "./components/nodes/encounter.jsx";
import Occurrence from "./components/nodes/occurrence.jsx";
import Bo from "./components/nodes/bo.jsx";
import Passage from "./components/nodes/passage.jsx";
import CoincidentPassages from "./components/nodes/coincident-passages.jsx";
import {useEdgeBuilder} from "./association-network-context.jsx";
import InfoConnection from "./components/info-connection.jsx";
import Menu from "./components/panels/menu.jsx";
import Details from "./components/panels/details.jsx";
import Config from "./components/panels/config.jsx";
import Actions from "./components/panels/actions.jsx";
import CustomControls from "./components/panels/custom-controls.jsx";
import KeysHandler from "./components/keys-handler.js";
import Search from "./components/panels/search.jsx";
import Init from "./components/panels/Init.jsx";
import SearchPassages from "./components/panels/search-passages.jsx";
import AddEdge from "./components/panels/add-edge.jsx";
import Swal from "sweetalert2";
import FloatingEdge from "./components/floating-edge.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import {resetActionFeedback} from "../../../../utils/action-feedback.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import I18nKeys from "./components/i18n-keys.js";
import Loader from "../../../../components/template/loader.jsx";
import Button from "../../../../components/button/button.jsx"
import SocialMedia from "./components/nodes/social-media.jsx";
import Company from "./components/nodes/company.jsx";
import News from "./components/nodes/news.jsx";
import Image from "./components/nodes/image.jsx";
import Annotation from "./components/nodes/annotation.jsx";
import NetworkMap from "./components/map/network-map.jsx";

const defaultEdgeOptions = {
    type: 'floating',
}

const analysisInitialData = {
    name: "",
    groups: []
}

const AssociationNetworkForm = ({id, cpf, plate, passage}) => {
    const navigate = navigateHook();
    const {panelOpened, setPanelOpened, viewMode, newEdge, isLoading, setIsLoading, showCancelRequestBtn, setShowCancelRequestBtn,
        images, setImages, setDeletedAttach, deletedAttach} = useEdgeBuilder();

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [analysisData, setAnalysisData] = useState(analysisInitialData);
    const [undoDisabled, setUndoDisabled] = useState(true)
    const [nameRequired, setNameRequired] = useState(false);
    const {cancel} = useAnalysis();

    const onConnect = useCallback((params) => {
        setEdges((eds) => addEdge(params, eds))
        setUndoDisabled(false)
    }, [setEdges]);

    useEffect(() => {
        if (!id && !cpf && !plate && !passage) setPanelOpened("init")
    }, [id]);

    const {refetch, isLoading: requestIsLoading} = useQuery({
        queryFn: () => associationNetworkService.getById(id)
            .then(({data}) => {
                const {edges, nodes, name, groups} = data;
                setNodes(nodes);
                setEdges(edges);
                setAnalysisData({name, groups});
                setUndoDisabled(true)

                return data;
            }).catch(() => {
                projectUtils.swalErrorMessage('', "Não foi possível carregar.", 'Ok')
            }),
        queryKey: ['association-network'],
        enabled: !!id,
    });

    const nodeTypes = {
        vehicle: Vehicle,
        individual: Individual,
        sos: Sos,
        encounter: Encounter,
        occurrence: Occurrence,
        bo: Bo,
        passage: Passage,
        coincidentPassages: CoincidentPassages,
        social_media: SocialMedia,
        company: Company,
        news: News,
        image: Image,
        annotation: Annotation,
    }

    const edgeTypes = useMemo(() => ({
        floating: FloatingEdge,
    }), []);

    const renameFile = (file, newName) => {
        const extension = file.name.split('.').pop();
        return new File([file], `${newName}.${extension}`, {
            type: file.type,
            lastModified: file.lastModified
        });
    }

    const save = () => {
        setIsLoading(true);
        setShowCancelRequestBtn(false);

        const data = {id, edges, nodes, name: analysisData.name, groups: analysisData.groups}

        const formData = new FormData()
        formData.append("data", JSON.stringify(data))
        formData.append("deleted_attach", deletedAttach.length ? JSON.stringify(deletedAttach) : "")

        if (images.length) {
            for (const image of images) {
                const renamedFile = renameFile(image.file, image.id);
                formData.append("files", renamedFile)
            }
        } else {
            formData.append("files", JSON.stringify([]))
        }

        associationNetworkService.save(formData)
            .then(({data}) => {
                projectUtils.success(!id);
                setImages([])

                if (id) {
                    refetch()
                    return;
                }

                const urlDev = `/association-network/form/${data}`;
                navigate(urlDev);
            })
            .catch((err) => {
                console.error(err)
                const {message} = err.response.data;

                let errMessage = I18nKeys[message] ?? "Não foi possível salvar.";
                if (message === "missing.name") {
                    resetActionFeedback();
                    Swal.fire({
                        icon: "error",
                        title: "",
                        text: errMessage,
                        confirmButtonText: "OK",
                        confirmButtonColor: "#063A6C",
                        reverseButtons: true,
                        returnFocus: false,
                    }).then(() => {
                        setNameRequired(true);
                        setPanelOpened("init");
                    });
                    return;
                }

                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })
            .finally(() => setIsLoading(false));
    }

    const undo = () => {
        Swal.fire({
            title: "Atenção",
            icon: "warning",
            html: "Descartar alterações não salvas? <br> Essa ação não pode ser desfeita.",
            confirmButtonText: "Sim",
            confirmButtonColor: "#063A6C",
            cancelButtonText: "Não",
            cancelButtonColor: "#c10007",
            showCancelButton: true,
            reverseButtons: true,
            preConfirm() {
                const confirmButton = Swal.getConfirmButton();

                Swal.disableButtons();

                if (confirmButton) {
                    confirmButton.classList.add("bg-[#9B9B9B]", "!opacity-100", "!flex", "!items-center", "!justify-center", "!gap-1");
                    confirmButton.style.setProperty("background-color", "#9B9B9B", "important");
                    confirmButton.setAttribute("aria-busy", "true");
                    confirmButton.innerHTML = '<span class="inline-block size-5 rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] animate-spin text-gray-300"></span> Desfazendo...';
                }

                setUndoDisabled(true)

                return refetch().then((result) => {
                    if (result?.isError || result?.isSuccess === false) throw result?.error ?? new Error("Não foi possível desfazer as alterações.");
                }).catch(() => {
                    setUndoDisabled(false);
                    Swal.enableButtons();
                    Swal.showValidationMessage("Não foi possível desfazer as alterações.");

                    if (!confirmButton) return false;

                    confirmButton.classList.remove("bg-[#9B9B9B]", "!opacity-100", "!flex", "!items-center", "!justify-center", "!gap-1");
                    confirmButton.style.removeProperty("background-color");
                    confirmButton.removeAttribute("aria-busy");
                    confirmButton.textContent = "Sim";
                    return false;
                });
            },
        })
    }

    const remove = (check) => {
        if (check) {
            Swal.fire({
                title: "Atenção",
                icon: "warning",
                html: `Excluir a rede de associação "${analysisData.name}"?<br /> 
                    Para confirmar, digite <b>excluir</b> abaixo:`,
                input: "text",
                confirmButtonText: "Confirmar",
                confirmButtonColor: "#063A6C",
                cancelButtonText: "Cancelar",
                showCancelButton: true,
                reverseButtons: true,
                preConfirm(inputValue) {
                    if (inputValue.trim() !== "excluir") {
                        Swal.showValidationMessage("Digite corretamente a palavra-chave");
                    }
                },
            }).then(({isConfirmed}) => {
                if (isConfirmed) remove(false);
            });

            return;
        }

        setIsLoading(true);
        setShowCancelRequestBtn(false);

        associationNetworkService.delete(id)
            .then(() => {
                const url = `/association-network`;
                navigate(url);
            })
            .catch((error) => {
                console.error(error);
                projectUtils.swalErrorMessage("", "Não foi possível excluir.", "Ok");
            })
            .finally(() => setIsLoading(false));
    }

    return (
        <>
            {
                isLoading &&
                <Loader textCenter={true}>
                    {
                        showCancelRequestBtn &&
                        <Button className={"mt-75"} onClick={cancel}>
                            Cancelar a analise
                        </Button>
                    }
                </Loader>
            }
            <Template isLoading={false}>

                <Template.Header>
                    <Template.Title>
                        {analysisData.name || "Rede de associação"}
                    </Template.Title>
                </Template.Header>
                <Template.Body className={"!p-0"}>
                    <div className={`relative ${newEdge ? "bg-[#cfcfcf]" : ""}`} style={{width: '100%', height: "calc(100vh - 54px)"}}>
                        <ReactFlow
                            className={"isolate"}
                            nodes={nodes}
                            edges={edges}
                            nodeTypes={nodeTypes}
                            edgeTypes={edgeTypes}
                            fitView
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            defaultEdgeOptions={defaultEdgeOptions}
                            onConnect={onConnect}
                            minZoom={0.1}
                            connectionMode={ConnectionMode.Loose}
                            isValidConnection={() => true}
                            onNodeDragStart={() => {
                                setNodes((nodes) =>
                                    nodes.map((n) => ({...n, selected: false}))
                                );
                            }}
                            onNodeDragStop={() => setUndoDisabled(false)}
                            onEdgesDelete={() => setUndoDisabled(false)}
                            onNodesDelete={(deletedNodes) => {
                                const attachIds = []
                                for (const deletedNode of deletedNodes) {
                                    if (deletedNode.attach_id) {
                                        attachIds.push(deletedNode.attach_id)
                                    }
                                }

                                if (attachIds.length) setDeletedAttach(prev => ([...prev, ...attachIds]))

                                setUndoDisabled(false)
                            }}
                        >
                            {
                                newEdge && viewMode === "flow" &&
                                <Panel position="top-center">
                                    <InfoConnection/>
                                </Panel>
                            }
                            {
                                viewMode === "flow" &&
                                <>
                                    <Panel position="top-right">
                                        <Details show={panelOpened === "details"}/>
                                        <Config
                                            setShowConfig={setPanelOpened}
                                            showConfig={panelOpened === "config"}
                                        />
                                    </Panel>
                                    <Panel position={"bottom-left"}>
                                        <Actions save={save} undo={undo} id={id} remove={remove} undoDisabled={undoDisabled}/>
                                    </Panel>
                                </>
                            }
                            <CustomControls />
                            <Background variant="dots" gap={12} size={1}/>
                            <KeysHandler/>
                        </ReactFlow>

                        {
                            viewMode === "map" &&
                            <div className={"absolute inset-0 z-10"}>
                                <NetworkMap nodes={nodes}/>
                            </div>
                        }

                        <div className={"absolute top-0 left-0 z-20 p-[15px]"}>
                            <Menu setPanelOpened={setPanelOpened}/>
                        </div>

                        <Search
                            show={panelOpened === "search"}
                            setAnalysisName={setAnalysisData}
                            cpf={cpf}
                            plate={plate}
                            passage={passage}
                        />

                        <Init
                            show={panelOpened === "init"}
                            analysisData={analysisData}
                            setAnalysisData={setAnalysisData}
                            nameRequired={nameRequired}
                            setNameRequired={setNameRequired}
                        />

                        <SearchPassages show={panelOpened === "passages"}/>
                        <AddEdge show={panelOpened === "addEdge"} setUndoDisabled={setUndoDisabled}/>
                    </div>
                </Template.Body>
            </Template>
        </>
    );
};

export default AssociationNetworkForm;